import React from "react";
import classNames from "classnames";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Input,
} from "reactstrap";

import { useGetStatistics } from "api/statistics/service";
import { useGetLogs } from "api/logs/service";
import DashboardScheduleExportsCalendar from "./dashboard/DashboardScheduleExportsCalendar";
import DashboardDailyWebTraffic from "./dashboard/DashboardDailyWebTraffic";
import { useCreateDashboard, useGetDashboards, useUpdateDashboard, useDeleteDashboard } from "api/dashboards/service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RiCalculatorFill, RiCarFill, RiSettings4Fill } from "react-icons/ri";
import { PiCarProfile } from "react-icons/pi";
import { HiArrowTrendingDown, HiArrowTrendingUp, HiMinusSmall } from "react-icons/hi2";
import {
  MdPeople,
  MdAccountBalanceWallet,
  MdAttachMoney,
  MdAssessment,
  MdAdd,
  MdBatchPrediction,
  MdList,
  MdDescription,
  MdHistory,
  MdDashboard,
  MdClose,
  MdEdit,
  MdShowChart,
  MdBarChart,
} from "react-icons/md";
import InventoryDashboard from "./dashboard/InventoryDashboard";
import SalesDashboard from "./dashboard/SalesDashboard";
import PaymentsDashboard from "./dashboard/PaymentsDashboard";
import SalesPersonDashboard from "./dashboard/SalesPersonDashboard";
import IQDashboard from "./dashboard/IQDashboard";
import CustomDashboard from "./dashboard/CustomDashboard";
import Select from "components/Select";
import SweetAlert from "react-bootstrap-sweetalert";
import { getActiveColorStyle, getButtonColor } from "../utils/colorUtils";
import { getSelectStyles } from "../utils/selectStyles";
import { formatDateTimeForDisplay } from "utils/dateUtils";
import RevenueByLocationMap from "components/Dashboard/RevenueByLocationMap";

/** Payment line/bar series — shared by chart datasets and header legend */
const PAYMENTS_COLOR_MAP = {
  local_lender_finance: "#2d7ec4",
  in_house_finance: "#1fa88a",
  cash: "#d4a03d",
  wholesale: "#7c5fd4",
};

const PAYMENTS_PERIOD_OPTIONS = [
  { value: "data1", label: "7d" },
  { value: "data2", label: "1m" },
  { value: "data3", label: "1y" },
];

/** Sales hub mini-card accent (icon tint + link) — fixed green, independent of theme primary. */
const DASHBOARD_SALES_HUB_GREEN = "#16a34a";
/** Sales person hub mini-card accent — violet (matches prior icon-success tone). */
const DASHBOARD_SALESPERSON_HUB_VIOLET = "#7c3aed";

/** Percent + direction → hub card trend row (inventory WoW, sales DoD, …). */
function computeTrendHubSummary(rawPercent, trendRaw) {
  let wowPct = null;
  if (rawPercent !== null && rawPercent !== undefined && rawPercent !== "") {
    const n = typeof rawPercent === "string" ? parseFloat(rawPercent, 10) : Number(rawPercent);
    if (Number.isFinite(n)) wowPct = n;
  }
  const trendNorm = typeof trendRaw === "string" ? trendRaw.trim().toLowerCase() : "";
  /** Locale-aware grouping (e.g. 10000 → 10,000 or 10.000) + up to 2 fraction digits. */
  const formatAbsPercentString = (n) => {
    if (n === null || !Number.isFinite(n)) return "0";
    const a = Math.abs(Math.round(n * 100) / 100);
    return a.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };
  let trendKind = "none";
  if (trendNorm === "up" || trendNorm === "increase" || trendNorm === "positive") {
    trendKind = "up";
  } else if (trendNorm === "down" || trendNorm === "decrease" || trendNorm === "negative") {
    trendKind = "down";
  } else if (trendNorm === "flat" || trendNorm === "neutral" || trendNorm === "same" || trendNorm === "unchanged") {
    trendKind = "flat";
  } else if (wowPct !== null) {
    if (Math.abs(wowPct) < 0.05) trendKind = "flat";
    else if (wowPct > 0) trendKind = "up";
    else trendKind = "down";
  }
  let percentDisplay = null;
  if (trendKind === "up") {
    percentDisplay = `+${formatAbsPercentString(wowPct ?? 0)}%`;
  } else if (trendKind === "down") {
    percentDisplay = `-${formatAbsPercentString(wowPct ?? 0)}%`;
  } else if (trendKind === "flat") {
    percentDisplay = `${formatAbsPercentString(wowPct ?? 0)}%`;
  }
  return { wowPct, trendKind, percentDisplay };
}

function DashboardHubTrendRow({ summary, vsLabelKey, isLightTheme }) {
  const { t } = useTranslation();
  const muted = isLightTheme ? "#94a3b8" : "#71717a";
  return (
    <div
      className="d-flex align-items-center justify-content-end mt-2 flex-nowrap"
      style={{
        gap: 6,
        minHeight: 20,
        width: "100%",
      }}
    >
      {summary.trendKind === "none" && (
        <span style={{ fontSize: "12px", fontWeight: 500, color: muted }}>
          {t("dashboard.inventoryTrendPending")}
        </span>
      )}
      {summary.trendKind === "up" && (
        <>
          <HiArrowTrendingUp size={18} color="#22c55e" aria-hidden style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#22c55e",
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {summary.percentDisplay}
          </span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: muted,
              marginLeft: 6,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {t(vsLabelKey)}
          </span>
        </>
      )}
      {summary.trendKind === "down" && (
        <>
          <HiArrowTrendingDown size={18} color="#f87171" aria-hidden style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#f87171",
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {summary.percentDisplay}
          </span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: muted,
              marginLeft: 6,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {t(vsLabelKey)}
          </span>
        </>
      )}
      {summary.trendKind === "flat" && (
        <>
          <HiMinusSmall size={18} color={isLightTheme ? "#64748b" : "#71717a"} aria-hidden style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: isLightTheme ? "#64748b" : "#a1a1aa",
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {summary.percentDisplay}
          </span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: muted,
              marginLeft: 6,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {t(vsLabelKey)}
          </span>
        </>
      )}
    </div>
  );
}

const Dashboard = (props) => {
  const { account, activeColor, previewMode, shellThemeVariant = "1" } = props;
  const hasLogsRead = account?.permissions?.logs_read === true;

  const { t } = useTranslation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (previewMode) return;
    if (account?.is_super_admin) {
      navigate("/tenant", { replace: true });
    }
  }, [account, navigate, previewMode]);

  const canReadAnalytics = account?.permissions?.dashboard_read_analytics === true;

  const [dashboardMode, setDashboardMode] = React.useState(() => {
    const savedMode = localStorage.getItem('dashboardViewType');
    if (!canReadAnalytics) return 'quick-menu';
    return savedMode || 'analytics';
  });

  React.useEffect(() => {
    const handleSync = () => {
      const savedMode = localStorage.getItem('dashboardViewType');
      if (!canReadAnalytics) {
        setDashboardMode('quick-menu');
      } else {
        setDashboardMode(savedMode || 'analytics');
      }
    };
    window.addEventListener('dashboard-view-changed', handleSync);
    // Initial check when account loads
    handleSync();
    return () => window.removeEventListener('dashboard-view-changed', handleSync);
  }, [canReadAnalytics]);

  const [isLightTheme, setIsLightTheme] = React.useState(
    () => typeof document !== "undefined" && document.body.classList.contains("white-content")
  );
  React.useEffect(() => {
    const checkTheme = () => setIsLightTheme(document.body.classList.contains('white-content'));
    checkTheme();

    // Watch for theme changes (some apps toggle className on body)
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const handleOpenDrawer = () => drawerRef.current?.open();
    window.addEventListener('dashboard-settings-drawer', handleOpenDrawer);
    return () => window.removeEventListener('dashboard-settings-drawer', handleOpenDrawer);
  }, []);
  const [bigChartData, setbigChartData] = React.useState("data1");
  const setBgChartData = (name) => {
    setbigChartData(name);
  };
  const [paymentsChartKind, setPaymentsChartKind] = React.useState("line");
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const drawerRef = React.useRef(null);
  const pastDueChartRef = React.useRef(null);

  const activeColorStyle = React.useMemo(
    () => getActiveColorStyle(props.activeColor || "blue"),
    [props.activeColor]
  );

  const paymentsPeriodSelectStyles = React.useMemo(() => {
    const base = getSelectStyles(activeColorStyle, isLightTheme);
    return {
      ...base,
      control: (provided, state) => ({
        ...base.control(provided, state),
        minHeight: "32px",
        height: "32px",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "0 6px",
        height: 30,
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: 30,
        paddingRight: 2,
      }),
      dropdownIndicator: (provided, state) => ({
        ...base.dropdownIndicator(provided, state),
        padding: "4px 4px 4px 2px",
      }),
      singleValue: (provided, state) => ({
        ...base.singleValue(provided, state),
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }),
      option: (provided, state) => ({
        ...base.option(provided, state),
        padding: "6px 10px",
        fontSize: 12,
      }),
    };
  }, [activeColorStyle, isLightTheme]);

  const [expandedRows, setExpandedRows] = React.useState({});
  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderChangedData = (changedData) => {
    if (!changedData) return <span className="text-muted" style={{ fontSize: '11px' }}>{t('dashboard.noChangesRecorded')}</span>;

    let data = changedData;
    if (typeof changedData === "string") {
      try {
        data = JSON.parse(changedData);
      } catch (e) {
        return <span style={{ fontSize: '12px' }}>{changedData}</span>;
      }
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      return (
        <div className="pl-3 py-3" style={{ background: 'rgba(0,0,0,0.01)', borderLeft: '3px solid #1d8cf8' }}>
          {Object.entries(data).map(([key, value], idx) => (
            <div key={idx} className="mb-1" style={{ fontSize: '12px' }}>
              <strong style={{ textTransform: 'capitalize', color: '#888' }}>{key.replace(/_/g, ' ')}:</strong> <span className="ml-1">{String(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    const nodes = Array.isArray(data) ? data : [];
    if (!nodes.length) return <span className="text-muted" style={{ fontSize: '11px' }}>-</span>;

    return (
      <div className="pl-3 py-3" style={{ background: 'rgba(0,0,0,0.01)', borderLeft: '3px solid #1d8cf8' }}>
        {nodes.map((node, idx) => {
          if (typeof node === "object" && node.children) {
            const text = (node?.children || [])
              .map((c) => (c && typeof c.text === "string" ? c.text : ""))
              .join("");
            return <p key={idx} className="mb-1" style={{ fontSize: '12px' }}>{text || "-"}</p>;
          }
          if (typeof node === "object") {
            return (
              <div key={idx} className="mb-2 border-bottom pb-2">
                {Object.entries(node).map(([k, v], i) => (
                  <div key={i} className="mb-1" style={{ fontSize: '12px' }}>
                    <strong style={{ textTransform: 'capitalize', color: '#888' }}>{k.replace(/_/g, ' ')}:</strong> <span className="ml-1">{String(v)}</span>
                  </div>
                ))}
              </div>
            );
          }
          return <p key={idx} className="mb-1" style={{ fontSize: '12px' }}>{String(node)}</p>;
        })}
      </div>
    );
  };
  const { data: statistics, isLoading: isStatsLoadingActual } = useGetStatistics(typeParam);
  const isStatsLoading = isStatsLoadingActual;
  const { data: logs } = useGetLogs(1, 10, [], { enabled: hasLogsRead });

  /** Main dashboard inventory tile — uses statistics.inventory week-over-week fields when present. */
  const inventoryHubSummary = React.useMemo(() => {
    const inv = statistics?.inventory;
    const total =
      inv?.total_inventory ??
      statistics?.total_inventory ??
      0;
    const raw =
      inv?.week_over_week_percent ??
      inv?.total_inventory_pct_change_vs_last_week ??
      inv?.week_over_week_pct_change ??
      statistics?.total_inventory_pct_change_vs_last_week ??
      statistics?.inventory_week_over_week_pct ??
      statistics?.total_inventory_trend?.pct_change;
    const trendRaw =
      inv?.week_over_week_trend ?? statistics?.total_inventory_trend?.direction;
    const trend = computeTrendHubSummary(raw, trendRaw);
    return { total, ...trend };
  }, [statistics]);

  /** Sales hub card — statistics.sales.today total + day-over-day trend. */
  const salesHubSummary = React.useMemo(() => {
    const st = statistics?.sales?.today;
    const raw =
      st?.day_over_day_percent ??
      st?.day_over_day_pct_change ??
      statistics?.sales_today_dod_percent;
    const trendRaw =
      st?.day_over_day_trend ??
      st?.day_over_day_direction ??
      statistics?.sales_today_dod_trend;
    const trend = computeTrendHubSummary(raw, trendRaw);
    let total = 0;
    const td = st?.total_data;
    if (td !== null && td !== undefined && td !== "") {
      const n = typeof td === "string" ? parseFloat(td, 10) : Number(td);
      if (Number.isFinite(n)) total = n;
    } else if (st?.data) {
      total =
        (Number(st.data.cash) || 0) +
        (Number(st.data.in_house_finance) || 0) +
        (Number(st.data.local_lender_finance) || 0) +
        (Number(st.data.wholesale) || 0);
    }
    const totalFormatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(total);
    return { total, totalFormatted, ...trend };
  }, [statistics]);

  /** Sales person hub card — statistics.sales_person (or legacy flat fields) + day-over-day trend. */
  const salesPersonHubSummary = React.useMemo(() => {
    const sp = statistics?.sales_person;
    const summaryText =
      sp && typeof sp === "object" && sp.sales_person_today != null && sp.sales_person_today !== ""
        ? String(sp.sales_person_today)
        : statistics?.sales_person_today != null && statistics?.sales_person_today !== ""
          ? String(statistics.sales_person_today)
          : "0 / $0";
    const raw =
      (sp && typeof sp === "object" ? sp.day_over_day_percent : null) ??
      statistics?.sales_person_day_over_day_percent;
    const trendRaw =
      (sp && typeof sp === "object" ? sp.day_over_day_trend : null) ??
      statistics?.sales_person_day_over_day_trend;
    const trend = computeTrendHubSummary(raw, trendRaw);
    return { summaryText, ...trend };
  }, [statistics]);

  const [isEditMode, setIsEditMode] = React.useState(false);
  const [menuItems, setMenuItems] = React.useState([]);
  const [tempMenuItems, setTempMenuItems] = React.useState([]);

  const defaultItems = React.useMemo(() => [
    { id: 'inventory-queues', name: t('routes.inventoryQueues'), icon: <MdList />, path: "/inventory-queues", permission: "inventory_queues_read" },
    { id: 'search-inventory', name: t('routes.searchInventory'), icon: <RiCarFill />, path: "/search-inventory", permission: "inventory_search_read" },
    { id: 'add-inventory', name: t('routes.addInventory'), icon: <MdAdd />, path: "/add-inventory", permission: "inventory_add" },
    { id: 'evaluate-inventory', name: t('routes.evaluateInventory'), icon: <MdBatchPrediction />, path: "/evaluate-inventory", permission: "inventory_evaluate_read" },
    { id: 'deals-queues', name: t('routes.dealsQueues'), icon: <MdList />, path: "/deals-queues", permission: "deals_queues_read" },
    { id: 'search-deals', name: t('routes.searchDeals'), icon: <RiCalculatorFill />, path: "/deals", permission: "deals_read" },
    { id: 'figure-a-deal', name: t('routes.figureADeal'), icon: <RiCalculatorFill />, path: "/figure-a-deal", permission: "deals_figure_read" },
    { id: 'add-retail-deal', name: t('routes.addRetailDeal'), icon: <MdAdd />, path: "/add-retail-deal", permission: "deals_retail_add" },
    { id: 'wholesale-deals', name: t('routes.wholesaleDeals'), icon: <MdAdd />, path: "/wholesale-deals", permission: "deals_wholesale_add" },
    { id: 'accounts', name: t('routes.accounts'), icon: <MdAccountBalanceWallet />, path: "/accounts", permission: "accounts_read" },
    { id: 'search-accounts', name: t('routes.searchAccounts'), icon: <MdAccountBalanceWallet />, path: "/accounts", permission: "accounts_read" },
    { id: 'reports', name: t('routes.reports'), icon: <MdAssessment />, path: "/reports", permission: "reports_read" },
    { id: 'standard-tax-reports', name: t('routes.standardTaxReports'), icon: <MdDescription />, path: "/standard-tax-reports", permission: "standard_tax_reports_read" },
    { id: 'customers', name: t('routes.customers'), icon: <MdPeople />, path: "/customers", permission: "customers_read" },
    { id: 'logs', name: t('dashboard.logs'), icon: <MdHistory />, path: "/logs", permission: "logs_read" },
    ...(account?.permissions?.read_payments ? [{ id: 'payments', name: t('routes.sales') || "Sales", icon: <MdAccountBalanceWallet />, path: "/payments" }] : [])
  ], [t, account?.permissions]);

  React.useEffect(() => {
    const savedOrder = localStorage.getItem('quickMenuOrder');
    let items = [];
    if (savedOrder) {
      const savedData = JSON.parse(savedOrder);
      items = savedData.map(savedItem => {
        const original = defaultItems.find(d => d.id === (savedItem.id || savedItem));
        if (!original) return null;
        return { ...original, name: savedItem.name || original.name };
      }).filter(Boolean);
    } else {
      items = [...defaultItems];
    }
    setMenuItems(items);
    setTempMenuItems(items);
  }, [defaultItems]);

  React.useEffect(() => {
    const handleSettingsMode = (e) => setIsEditMode(e.detail);
    const handleSave = () => {
      const savedData = tempMenuItems.map(item => ({ id: item.id, name: item.name }));
      localStorage.setItem('quickMenuOrder', JSON.stringify(savedData));
      setMenuItems(tempMenuItems);
    };
    const handleCancel = () => setTempMenuItems(menuItems);
    const handleReset = () => {
      localStorage.removeItem('quickMenuOrder');
      setMenuItems(defaultItems);
      setTempMenuItems(defaultItems);
      setIsEditMode(false);
    };

    window.addEventListener('menu-settings-mode', handleSettingsMode);
    window.addEventListener('save-menu-settings', handleSave);
    window.addEventListener('cancel-menu-settings', handleCancel);
    window.addEventListener('reset-menu-settings', handleReset);

    return () => {
      window.removeEventListener('menu-settings-mode', handleSettingsMode);
      window.removeEventListener('save-menu-settings', handleSave);
      window.removeEventListener('cancel-menu-settings', handleCancel);
      window.removeEventListener('reset-menu-settings', handleReset);
    };
  }, [tempMenuItems, menuItems, defaultItems]);

  const removeItem = (id) => {
    setTempMenuItems(prev => prev.filter(item => item.id !== id));
  };

  const [showAddModal, setShowAddModal] = React.useState(false);

  const addItem = (item) => {
    setTempMenuItems(prev => [...prev, item]);
    setShowAddModal(false);
  };

  const [editingItemId, setEditingItemId] = React.useState(null);
  const [editingValue, setEditingValue] = React.useState('');

  const renameItem = (id, newName) => {
    setTempMenuItems(prev => prev.map(item =>
      item.id === id ? { ...item, name: newName } : item
    ));
    setEditingItemId(null);
  };

  // HTML5 Drag and Drop handlers
  const [draggedId, setDraggedId] = React.useState(null);

  const handleDragStart = (e, id) => {
    if (!isEditMode) return;
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
    // Set a ghost image or just let it be default
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (!isEditMode || draggedId === null || draggedId === id) return;

    const newItems = [...tempMenuItems];
    const draggedIndex = newItems.findIndex(item => item.id === draggedId);
    const targetIndex = newItems.findIndex(item => item.id === id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const item = newItems[draggedIndex];
      newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, item);
      setTempMenuItems(newItems);
    }
  };

  const handleDragEnd = (e) => {
    setDraggedId(null);
    e.currentTarget.style.opacity = '1';
  };
  const paymentsObj = statistics?.payments || {
    weekly: { labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], data: {}, counts: {} },
    monthly: { labels: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"], data: {}, counts: {} },
    yearly: { labels: ["2022", "2023", "2024", "2025", "2026"], data: {}, counts: {} },
  };

  let totalAmount = 0;
  let totalCount = 0;

  const currentPeriodKey = bigChartData === 'data1' ? 'weekly' : bigChartData === 'data2' ? 'monthly' : 'yearly';
  const currentPeriodData = paymentsObj[currentPeriodKey];

  if (currentPeriodData?.data) {
    Object.values(currentPeriodData.data).forEach(arr => {
      arr.forEach(val => {
        totalAmount += val;
      });
    });
  }

  if (currentPeriodData?.counts) {
    Object.values(currentPeriodData.counts).forEach(arr => {
      arr.forEach(val => {
        totalCount += val;
      });
    });
  }

  const createMultiChartData = (canvas, labels, dataObj) => {
    let ctx = canvas.getContext("2d");

    const createGradient = (color) => {
      let gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
      const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      gradientStroke.addColorStop(1, hexToRgba(color, 0.2));
      gradientStroke.addColorStop(0.4, hexToRgba(color, 0.0));
      gradientStroke.addColorStop(0, hexToRgba(color, 0));
      return gradientStroke;
    };

    const datasets = [];
    const labelMap = {
      local_lender_finance: t('dashboard.localLender'),
      in_house_finance: t('dashboard.inHouse'),
      cash: t('dashboard.cash'),
      wholesale: t('dashboard.wholesale')
    };

    if (dataObj) {
      Object.keys(dataObj).forEach(key => {
        const color = PAYMENTS_COLOR_MAP[key] || "#1f8ef1";
        datasets.push({
          label: labelMap[key] || key,
          fill: true,
          backgroundColor: createGradient(color),
          borderColor: color,
          borderWidth: 2,
          borderDash: [],
          borderDashOffset: 0.0,
          pointBackgroundColor: color,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 0,
          pointRadius: 0,
          pointHitRadius: 20,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: "#ffffff",
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          data: dataObj[key],
        });
      });
    }

    return {
      labels: labels || [],
      datasets
    };
  };
  const dynamicChartData = {
    data1: (canvas) => createMultiChartData(canvas, paymentsObj.weekly?.labels, paymentsObj.weekly?.data),
    data2: (canvas) => createMultiChartData(canvas, paymentsObj.monthly?.labels, paymentsObj.monthly?.data),
    data3: (canvas) => createMultiChartData(canvas, paymentsObj.yearly?.labels, paymentsObj.yearly?.data),
    options: {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false,
          position: 'bottom',
          labels: {
            color: isLightTheme ? '#64748b' : '#cbd5e1',
          }
        },
      },
      scales: {
        y: {
          grid: {
            drawBorder: false,
            color: activeColorStyle?.borderColor || "rgba(29, 140, 248, 0.1)",
            zeroLineColor: "transparent",
            borderWidth: 0,
            lineWidth: 0.1,
          },
          ticks: {
            suggestedMin: 60,
            suggestedMax: 125,
            padding: 20,
            color: isLightTheme ? "#64748b" : "#cbd5e1",
          },
        },

        x: {
          grid: {
            drawBorder: false,
            color: activeColorStyle?.borderColor || "rgba(29, 140, 248, 0.1)",
            zeroLineColor: "transparent",
            borderWidth: 0,
            lineWidth: 0.1,
          },
          ticks: {
            padding: 20,
            color: isLightTheme ? "#64748b" : "#cbd5e1",
          },
        },
      },
    },
  };

  const buildPaymentsBarChartData = (labels, dataObj) => {
    const labelMap = {
      local_lender_finance: t("dashboard.localLender"),
      in_house_finance: t("dashboard.inHouse"),
      cash: t("dashboard.cash"),
      wholesale: t("dashboard.wholesale"),
    };
    const datasets = [];
    if (dataObj) {
      Object.keys(dataObj).forEach((key) => {
        const color = PAYMENTS_COLOR_MAP[key] || "#1f8ef1";
        datasets.push({
          label: labelMap[key] || key,
          data: dataObj[key],
          backgroundColor: `${color}55`,
          borderColor: color,
          borderWidth: 2,
        });
      });
    }
    return { labels: labels || [], datasets };
  };

  const paymentsBarChartData = buildPaymentsBarChartData(
    currentPeriodData?.labels,
    currentPeriodData?.data
  );

  const paymentsLegendItems = React.useMemo(
    () =>
      Object.keys(PAYMENTS_COLOR_MAP).map((key) => ({
        key,
        color: PAYMENTS_COLOR_MAP[key],
        label:
          key === "local_lender_finance"
            ? t("dashboard.localLender")
            : key === "in_house_finance"
              ? t("dashboard.inHouse")
              : key === "cash"
                ? t("dashboard.cash")
                : t("dashboard.wholesale"),
      })),
    [t]
  );

  const paymentsPeriodSelectValue =
    bigChartData === "data1"
      ? PAYMENTS_PERIOD_OPTIONS[0]
      : bigChartData === "data2"
        ? PAYMENTS_PERIOD_OPTIONS[1]
        : PAYMENTS_PERIOD_OPTIONS[2];

  const getPaymentsLineOrBarOptions = (kind) => {
    const isBar = kind === "bar";
    return {
      maintainAspectRatio: false,
      responsive: true,
      interaction: { mode: "index", intersect: false },
      ...(isBar
        ? {}
        : {
            elements: {
              line: {
                tension: 0.42,
                borderWidth: 2.5,
                borderCapStyle: "round",
                borderJoinStyle: "round",
              },
              point: {
                radius: 0,
                hoverRadius: 5,
                hitRadius: 20,
                hoverBorderWidth: 2,
                hoverBackgroundColor: "#3b82f6",
                hoverBorderColor: "#ffffff",
              },
            },
          }),
      plugins: {
        ...dynamicChartData.options.plugins,
        tooltip: {
          backgroundColor: isLightTheme ? "#121826" : "#0f172a",
          titleColor: "#ffffff",
          bodyColor: "#d1d5db",
          borderColor: "rgba(59, 130, 246, 0.4)",
          borderWidth: 1,
          displayColors: true,
          padding: 10,
          caretSize: 6,
        },
      },
      scales: {
        x: {
          ...dynamicChartData.options.scales?.x,
          grid: {
            ...dynamicChartData.options.scales?.x?.grid,
            display: false,
          },
          ticks: {
            ...dynamicChartData.options.scales?.x?.ticks,
            color: isLightTheme ? "#6b7280" : "#9ca3af",
            maxRotation: 0,
          },
        },
        y: isBar
          ? {
              grid: {
                drawBorder: false,
                color: activeColorStyle?.borderColor || "rgba(29, 140, 248, 0.1)",
                zeroLineColor: "transparent",
                borderWidth: 0,
                lineWidth: 0.1,
                display: true,
              },
              beginAtZero: true,
              ticks: {
                padding: 20,
                color: isLightTheme ? "#6b7280" : "#9ca3af",
              },
            }
          : {
              ...dynamicChartData.options.scales?.y,
              ticks: {
                ...dynamicChartData.options.scales?.y?.ticks,
                color: isLightTheme ? "#6b7280" : "#9ca3af",
              },
            },
      },
      animation: {
        duration: 1000,
        easing: "easeOutQuart",
        delay: (c) => c.dataIndex * 100,
      },
    };
  };

  // Fixed colors for multi-colored charts
  const chartColors = [
    "#2563eb",
    "#0d9488",
    "#8b5cf6",
    "#d97706",
    "#059669",
    "#7c3aed",
    "#dc2626",
  ];

  // Vehicles küçük grafiği için API'den gelen verileri kullan
  const vehicleMonths = statistics?.inventory_by_location_monthly?.months || ["JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const vehicleValues = statistics?.inventory_by_location_monthly?.vehicles || [0, 0, 0, 0, 0, 0];
  const vehicleChartData = (canvas) => {
    return {
      labels: vehicleMonths,
      datasets: [
        {
          label: "",
          fill: true,
          backgroundColor: vehicleMonths.map((_, i) => chartColors[i % chartColors.length]),
          borderColor: vehicleMonths.map((_, i) => chartColors[i % chartColors.length]),
          borderWidth: 2,
          data: vehicleValues,
          barPercentage: 0.7,
        },
      ],
    };
  };

  // Past Due Doughnut Chart
  const pastDueData = statistics?.accounts_past_due || {};
  const pastDueLabels = [
    "Current",
    "T1 - Past Due 1 - 15",
    "T2 - Past Due 16 - 30",
    "T3 - Past Due 31 - 60",
    "T4 - Past Due 61 - 90",
    "T5 - Past Due 91 - 120",
    "T6 - Past Due > 120",
    "T7 - Repossession",
    "T8 - Bankruptcy"
  ];
  const pastDueValues = [
    pastDueData.t0 || 0,
    pastDueData.t1 || 0,
    pastDueData.t2 || 0,
    pastDueData.t3 || 0,
    pastDueData.t4 || 0,
    pastDueData.t5 || 0,
    pastDueData.t6 || 0,
    pastDueData.t7 || 0,
    pastDueData.t8 || 0,
  ];

  const pastDueColors = [
    "#22a06b",
    "#2fbf7a",
    "#5cb85c",
    "#d4a03d",
    "#e8943a",
    "#e07b2d",
    "#d45a3a",
    "#c23b3b",
    "#9b1c31",
  ];

  const pastDueChartData = {
    labels: pastDueLabels,
    datasets: [
      {
        label: "Accounts Past Due",
        data: pastDueValues,
        backgroundColor: pastDueColors,
        hoverBackgroundColor: pastDueColors,
        borderWidth: 0,
        spacing: 0,
        hoverOffset: 30,
      },
    ],
  };

  const doughnutLabelsLine = React.useMemo(() => ({
    id: 'doughnutLabelsLine',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      const meta = chart.getDatasetMeta(0);
      const light =
        typeof document !== "undefined" && document.body.classList.contains("white-content");

      ctx.save();
      meta.data.forEach((element, index) => {
        const value = Number(data?.datasets?.[0]?.data?.[index] || 0);
        if (!value) return;

        const { x, y, startAngle, endAngle, outerRadius } = element;
        const middleAngle = startAngle + (endAngle - startAngle) / 2;
        const startX = x + Math.cos(middleAngle) * outerRadius;
        const startY = y + Math.sin(middleAngle) * outerRadius;
        const endX = x + Math.cos(middleAngle) * (outerRadius + 10);
        const endY = y + Math.sin(middleAngle) * (outerRadius + 10);
        const finalX = endX + (Math.cos(middleAngle) > 0 ? 8 : -8);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.lineTo(finalX, endY);
        ctx.strokeStyle = light ? "rgba(148, 163, 184, 0.8)" : "rgba(255, 255, 255, 0.28)";
        ctx.lineWidth = 1;
        ctx.stroke();

        const textValue = String(value);
        ctx.font = "600 13px Inter, Arial, sans-serif";
        ctx.fillStyle = light ? "#334155" : "rgba(255,255,255,0.88)";
        const isRightSide = Math.cos(middleAngle) > 0;
        ctx.textAlign = isRightSide ? "left" : "right";
        ctx.textBaseline = "middle";
        const textWidth = ctx.measureText(textValue).width;
        const edgePad = 10;
        let labelX = finalX + (isRightSide ? 4 : -4);
        if (isRightSide) {
          if (labelX + textWidth > chart.width - edgePad) {
            labelX = chart.width - edgePad - textWidth;
          }
        } else if (labelX - textWidth < edgePad) {
          labelX = edgePad + textWidth;
        }
        ctx.fillText(textValue, labelX, endY);
      });
      ctx.restore();
    },
  }), []);

  const pastDueLegendItems = React.useMemo(() => {
    const total = pastDueValues.reduce((sum, value) => sum + Number(value || 0), 0);
    return pastDueLabels
      .map((label, index) => {
        const value = Number(pastDueValues[index] || 0);
        if (value <= 0) return null;
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
        return {
          index,
          label,
          value,
          percentage,
          color: pastDueColors[index],
        };
      })
      .filter(Boolean);
  }, [pastDueColors, pastDueLabels, pastDueValues]);

  const pastDueLegendColumns = React.useMemo(() => {
    const midpoint = Math.ceil(pastDueLegendItems.length / 2);
    return [
      pastDueLegendItems.slice(0, midpoint),
      pastDueLegendItems.slice(midpoint),
    ];
  }, [pastDueLegendItems]);

  const setPastDueLegendHover = React.useCallback((index = null) => {
    const chart = pastDueChartRef.current;
    if (!chart) return;

    if (index == null) {
      chart.setActiveElements([]);
      chart.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
      chart.update();
      return;
    }

    const meta = chart.getDatasetMeta(0);
    const arc = meta?.data?.[index];
    if (!arc) return;

    const { x, y } = arc.getProps(["x", "y"], true);
    const active = [{ datasetIndex: 0, index }];
    chart.setActiveElements(active);
    chart.tooltip?.setActiveElements?.(active, { x, y });
    chart.update();
  }, []);

  const pastDueChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: "78%",
    rotation: -90,
    circumference: 180,
    layout: {
      padding: {
        left: 24,
        right: 24,
        top: 28,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1e1e2f",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        footerColor: "#ffffff",
        displayColors: true,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
            const index = context.dataIndex;
            const displayLabel = label.startsWith(`T${index}`) ? label : `T${index} - ${label}`;
            return `${displayLabel}, ${percentage}%`;
          }
        }
      },
    },
  };

  // Vehicles grafiği için yatay (horizontal) görünüm ve ondalık göstermemek adına özel seçenekler
  const maxVehicleValue = Math.max(...vehicleValues, 0);

  const vehicleChartOptions = {
    ...dynamicChartData.options,
    indexAxis: 'y',
    plugins: {
      ...dynamicChartData.options.plugins,
      legend: {
        display: false,
      },
    },
    scales: {
      ...dynamicChartData.options.scales,
      x: {
        ...dynamicChartData.options.scales.x,
        suggestedMax: maxVehicleValue + 1,
        ticks: {
          ...dynamicChartData.options.scales.x.ticks,
          stepSize: 1,
          precision: 0,
          callback: function (value) {
            if (Number.isInteger(value)) {
              return value;
            }
          },
        },
      },
      y: {
        ...dynamicChartData.options.scales.y,
      },
    },
  };

  const totalPastDue = pastDueValues.reduce((a, b) => a + b, 0);

  const salesTodayObj = statistics?.sales?.today || {
    data: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
    counts: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
    total_data: 0,
    total_counts: 0
  };


  const salesTodayLabels = ["Cash", "In-House", "Outside", "Wholesale"];
  const salesTodayValues = [
    salesTodayObj.data?.cash || 0,
    salesTodayObj.data?.in_house_finance || 0,
    salesTodayObj.data?.local_lender_finance || 0,
    salesTodayObj.data?.wholesale || 0
  ];
  const salesTodayCounts = [
    salesTodayObj.counts?.cash || 0,
    salesTodayObj.counts?.in_house_finance || 0,
    salesTodayObj.counts?.local_lender_finance || 0,
    salesTodayObj.counts?.wholesale || 0
  ];
  const salesTodayColors = ["#d1b975", "#6aa0c3", "#a8cbac", "#e7d9d0"];

  const salesTodayChartData = {
    labels: salesTodayLabels,
    datasets: [
      {
        label: "Sales Today",
        data: salesTodayValues,
        backgroundColor: salesTodayColors,
        hoverBackgroundColor: salesTodayColors,
        borderWidth: 0,
      },
    ],
  };

  const salesTodayChartOptions = {
    ...pastDueChartOptions,
    plugins: {
      ...pastDueChartOptions.plugins,
      tooltip: {
        enabled: !isStatsLoading,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const val = context.raw || 0;
            const total = salesTodayObj.total_data || 1;
            const perc = ((val / total) * 100).toFixed(0);
            return `${label}: $${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${perc}%)`;
          }
        }
      }
    }
  };


  return (
    <>
      <style>
        {`
        @media (max-width: 991px) {
            .card-first, .card-second, .card-third, .card-spacing-fix {
              padding-left: 0px !important;
              padding-right: 15px !important;
            }
          }
          @media (max-width: 768px) {
            .card-spacing-fix {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
          }

          .card-modern {
            border-radius: 0px !important;
            border: 1px solid rgba(255, 255, 255, 0.05) !important;
            transition: none;
            overflow: hidden;
            background: rgba(30, 30, 47, 0.95);
          }
          
          .white-content .card-modern {
            background: #ffffff !important;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02) !important;
            border: 1px solid rgba(0, 0, 0, 0.03) !important;
          }

          .dashboard-analytics-chart-block .card-chart .card-header {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .dashboard-analytics-chart-block .card-chart .card-body {
            padding-left: 3px !important;
            padding-right: 3px !important;
          }

          .dashboard-logs-section {
            align-items: stretch;
          }
          .dashboard-logs-section > [class*="col-"] {
            display: flex;
          }
          .dashboard-logs-section .dashboard-split-panel-card {
            flex: 1;
            width: 100%;
            display: flex;
            flex-direction: column;
            height: 420px;
          }
          .dashboard-logs-section .dashboard-split-panel-card > .card-header {
            padding: 15px 15px 0 !important;
            flex-shrink: 0;
          }
          .dashboard-logs-section .dashboard-split-panel-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            padding: 8px 15px 15px !important;
          }
          .dashboard-logs-section .dashboard-split-panel-body.dashboard-split-panel-body--flush {
            padding: 0 !important;
          }
          .dashboard-logs-section .dashboard-panel-header,
          .dashboard-logs-section .schedule-exports-calendar-header {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 15px 15px 0;
            margin-bottom: 0;
            gap: 12px;
            flex-wrap: nowrap;
          }
          .dashboard-logs-section .schedule-exports-calendar-header .card-category {
            text-transform: none !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            color: rgba(255, 255, 255, 0.95) !important;
          }
          .white-content .dashboard-logs-section .card-modern .schedule-exports-calendar-header .card-category {
            color: #9a9a9a !important;
          }
          .dashboard-logs-section .schedule-exports-calendar.schedule-exports-calendar--fill {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .dashboard-logs-section .schedule-exports-calendar.schedule-exports-calendar--fill .schedule-exports-calendar-grid {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
            margin: 8px 15px 15px;
          }
          .dashboard-logs-section
            .schedule-exports-calendar.schedule-exports-calendar--fill.schedule-exports-calendar--detail
            .schedule-exports-calendar-detail-scroll {
            margin: 8px 15px 15px;
          }
          .dashboard-logs-section .dashboard-logs-scroll {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
          }
          .dashboard-logs-section .dashboard-logs-col-left {
            padding-left: 15px !important;
            padding-right: 5px !important;
          }
          .dashboard-logs-section .dashboard-logs-col-right {
            padding-left: 5px !important;
            padding-right: 15px !important;
          }

          .btn-see-detail {
            color: #1d8cf8;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            cursor: pointer;
            padding: 5px 0px;
            border-radius: 0px;
            user-select: none;
          }

          .btn-see-detail:hover {
            text-decoration: underline !important;
            cursor: pointer;
          }
          
          .card-modern .card-category,
          .card-modern .card-header .card-category,
          .card-modern .card-body .card-category {
            text-transform: none !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            color: rgba(255, 255, 255, 0.95) !important;
          }

          .white-content .card-modern .card-category,
          .white-content .card-modern .card-header .card-category,
          .white-content .card-modern .card-body .card-category {
            color: #9a9a9a !important;
          }
          
          .card-modern-title {
            font-size: 13px !important;
            color: rgba(255, 255, 255, 0.95) !important;
            font-weight: 500;
          }

          .white-content .card-modern .card-modern-title {
            color: #6c757d !important;
          }
          
          .card-modern .card-modern-value {
            font-size: 15px !important;
            font-weight: 600 !important;
            color: rgba(255, 255, 255, 0.96) !important;
          }

          .white-content .card-modern .card-modern-value {
            color: #111111 !important;
          }

          .card-modern .dashboard-payments-total-amount {
            font-size: 1.1rem !important;
            font-weight: 700 !important;
            line-height: 1.15 !important;
            color: rgba(255, 255, 255, 0.96) !important;
            letter-spacing: -0.02em;
            display: block;
          }
          .white-content .card-modern .dashboard-payments-total-amount {
            color: #111111 !important;
          }
          
          .table-modern thead th {
            text-transform: uppercase !important;
            font-size: 0.7rem !important;
            font-weight: 700 !important;
            padding: 10px 10px !important;
            letter-spacing: 0.5px;
            border-top: none !important;
            border-bottom: 1px solid rgba(0,0,0,0.05) !important;
          }  
            
          .expanded-row-data {
            padding: 0px !important;
            margin: 0px !important;
          }
            .table-modern tbody tr .expanded-row-data {
            padding: 0px !important;
            margin: 0px !important;
          }
          .table-modern tbody td {
            font-size: 0.85rem !important;
            padding: 8px 10px !important;
            vertical-align: middle !important;
          }
        
          
          .table-modern tbody tr:nth-of-type(odd) {
            background-color: rgba(0, 0, 0, 0.02) !important;
          }
          
          .log-user-tag {
            background: rgba(29, 140, 248, 0.08);
            color: #1d8cf8;
            padding: 4px 12px;
            border-radius: 0px;
            font-weight: 600;
            font-size: 0.8rem;
            cursor: pointer;
            display: inline-block;
            transition: all 0.2s;
            text-decoration: underline;
          }

          .log-user-tag:hover {
            background: rgba(29, 140, 248, 0.15);
            transform: scale(1.05);
          }
          
          .log-action-link {
            color: #1d8cf8;
            font-weight: 500;
            cursor: pointer;
            text-decoration: underline;
          }

          .log-action-link:hover {
            color: #0d6efd;
            text-decoration: underline;
          }

          .skeleton-shimmer {
            background: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite linear;
            border-radius: 4px;
          }

          .white-content .skeleton-shimmer {
            background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
            background-size: 200% 100%;
          }

          @keyframes shimmer {
            to {
              background-position: -200% 0;
            }
          }

          .loading-ghost-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(255,255,255,0.1);
            backdrop-filter: blur(1px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100;
            pointer-events: none;
          }
          
          .pulse-loading {
            font-weight: 600;
            font-size: 14px;
            color: #1d8cf8;
            text-transform: uppercase;
            letter-spacing: 2px;
            animation: pulse 1.5s infinite ease-in-out;
          }
          
          @keyframes pulse {
            0% { opacity: 0.4; transform: scale(0.98); }
            50% { opacity: 1; transform: scale(1); }
            100% { opacity: 0.4; transform: scale(0.98); }
          }

          .card-modern.card-stats .info-icon {
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14);
            border: none !important;
          }
          .card-modern.card-stats .info-icon.icon-info {
            background: linear-gradient(145deg, #2563eb 0%, #1d4ed8 100%) !important;
            background-image: linear-gradient(145deg, #2563eb 0%, #1d4ed8 100%) !important;
          }
          .card-modern.card-stats .info-icon.icon-primary {
            background: linear-gradient(145deg, #0d9488 0%, #0f766e 100%) !important;
            background-image: linear-gradient(145deg, #0d9488 0%, #0f766e 100%) !important;
          }
          .card-modern.card-stats .info-icon.icon-success {
            background: linear-gradient(145deg, #8b5cf6 0%, #6d28d9 100%) !important;
            background-image: linear-gradient(145deg, #8b5cf6 0%, #6d28d9 100%) !important;
          }
          .white-content .card-modern.card-stats .info-icon.icon-info {
            background: linear-gradient(145deg, #3b82f6 0%, #2563eb 100%) !important;
            background-image: linear-gradient(145deg, #3b82f6 0%, #2563eb 100%) !important;
          }
          .white-content .card-modern.card-stats .info-icon.icon-primary {
            background: linear-gradient(145deg, #14b8a6 0%, #0d9488 100%) !important;
            background-image: linear-gradient(145deg, #14b8a6 0%, #0d9488 100%) !important;
          }
          .white-content .card-modern.card-stats .info-icon.icon-success {
            background: linear-gradient(145deg, #a78bfa 0%, #7c3aed 100%) !important;
            background-image: linear-gradient(145deg, #a78bfa 0%, #7c3aed 100%) !important;
          }
        `}
      </style>
      <div
        className={classNames("content", {
          "theme2-admin-content-compact": shellThemeVariant === "2",
          "theme2-dashboard-pad-end": shellThemeVariant === "2" && !typeParam,
          "theme2-inventory-search-inset": shellThemeVariant === "2" && !!typeParam,
        })}
      >
        {dashboardMode === 'analytics' ? (
          <>
            {typeParam === 'inventory' ? (
              <InventoryDashboard
                activeColor={activeColor}
                statistics={statistics}
                isLoading={isStatsLoading}
                shellThemeVariant={shellThemeVariant}
                isLightTheme={isLightTheme}
              />
            ) : typeParam === 'sales' ? (
              <SalesDashboard
                activeColor={activeColor}
                statistics={statistics}
                isLoading={isStatsLoading}
                shellThemeVariant={shellThemeVariant}
                isLightTheme={isLightTheme}
              />
            ) : typeParam === 'payments' ? (
              <PaymentsDashboard
                activeColor={activeColor}
                statistics={statistics}
                isLoading={isStatsLoading}
                shellThemeVariant={shellThemeVariant}
                isLightTheme={isLightTheme}
              />
            ) : typeParam === 'salesperson' ? (
              <SalesPersonDashboard
                activeColor={activeColor}
                statistics={statistics}
                isLoading={isStatsLoading}
                shellThemeVariant={shellThemeVariant}
                isLightTheme={isLightTheme}
              />
            ) : typeParam === 'iq' ? (
              <IQDashboard
                activeColor={activeColor}
                statistics={statistics}
                isLoading={isStatsLoading}
                shellThemeVariant={shellThemeVariant}
                isLightTheme={isLightTheme}
              />
            ) : typeParam ? (
              <CustomDashboard dashboardId={typeParam} isLightTheme={isLightTheme} />
            ) : (
              <>
                <style>{`
                  body .dashboard-hub-card,
                  body .dashboard-hub-card .card-body {
                    background: ${activeColorStyle.background} !important;
                    background-color: ${activeColorStyle.borderColor} !important;
                    border: none !important;
                    transition: all 0.3s ease !important;
                  }
                  body .dashboard-hub-card:hover {
                    transform: translateY(-4px) !important;
                    box-shadow: 0 8px 25px rgba(29, 140, 248, 0.4) !important;
                  }
                  body .dashboard-hub-card * {
                    color: white !important;
                  }
                  body .dashboard-hub-card .info-icon {
                    background: rgba(255, 255, 255, 0.2) !important;
                  }
                `}</style>
                <Row className="equal-height-cards " style={{}}>
                  <Col lg="3" md="6" className="d-flex card-spacing-fix" style={{ paddingLeft: '15px', paddingRight: '10px' }}>
                    <div
                      className="card card-stats card-modern flex-fill dashboard-hub-card"
                      onClick={() => drawerRef.current?.open()}
                      style={{
                        cursor: 'pointer',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 0 15px rgba(29, 140, 248, 0.1)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardBody className="d-flex flex-column align-items-center justify-content-center" style={{ height: '100%', minHeight: '100px' }}>
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <div style={{
                            width: '45px',
                            height: '45px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '10px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>
                            <RiSettings4Fill color="#fff" size={24} />
                          </div>
                          <div className="text-center">
                            <h4 style={{ margin: 0, fontWeight: '800', fontSize: '11px', letterSpacing: '1.2px', color: '#fff', textTransform: 'uppercase' }}>
                              Dashboard Hub
                            </h4>
                            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.7)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Settings & Navigation
                            </p>
                          </div>
                        </div>
                      </CardBody>
                    </div>
                  </Col>
                  <Col lg="3" md="6" className="d-flex card-spacing-fix card-first" style={{ paddingLeft: '0px', paddingRight: '10px', }}>
                    <Card className="card-stats card-modern flex-fill dashboard-inventory-stat-card" onClick={() => navigate('?type=inventory')} style={{ cursor: 'pointer' }}>
                      <CardBody >
                        <Row style={{ height: "100%" }}>
                          <Col xs="4" className="d-flex align-items-center justify-content-start">
                            <div
                              className="d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: 50,
                                height: 50,
                                border: "none",
                                backgroundColor: `color-mix(in srgb, ${activeColorStyle.borderColor} 12%, transparent)`,
                              }}
                              aria-hidden
                            >
                              <PiCarProfile color={activeColorStyle.borderColor} size={26} />
                            </div>
                          </Col>
                          <Col xs="8" className="d-flex align-items-center justify-content-end p-0 pr-3">
                            <div className="numbers text-right d-flex flex-column align-items-end" style={{ width: "100%" }}>
                              <div
                                className="btn-see-detail"
                                style={{ color: activeColorStyle.borderColor }}
                                onClick={() => navigate('?type=inventory')}
                              >
                                {t("dashboard.inventoryDashboardLink")}
                              </div>
                              <p className="card-modern-title mb-1 mt-1">{t('dashboard.totalVehicles')}</p>
                              <h3
                                className="card-modern-value mb-0"
                                style={{ fontWeight: 700, fontSize: "20px", letterSpacing: "-0.02em", lineHeight: 1.15 }}
                              >
                                {inventoryHubSummary.total}
                              </h3>
                              <DashboardHubTrendRow
                                summary={inventoryHubSummary}
                                vsLabelKey="dashboard.inventoryTrendVsLastWeek"
                                isLightTheme={isLightTheme}
                              />
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col lg="3" md="6" className="d-flex card-spacing-fix card-second" style={{ paddingLeft: '0px', paddingRight: '10px', }}>
                    <Card className="card-stats card-modern flex-fill dashboard-sales-stat-card" onClick={() => navigate('?type=sales')} style={{ cursor: 'pointer' }}>
                      <CardBody>
                        <Row style={{ height: "100%" }}>
                          <Col xs="4" className="d-flex align-items-center justify-content-start">
                            <div
                              className="d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: 50,
                                height: 50,
                                border: "none",
                                backgroundColor: `color-mix(in srgb, ${DASHBOARD_SALES_HUB_GREEN} 14%, transparent)`,
                              }}
                              aria-hidden
                            >
                              <MdAttachMoney color={DASHBOARD_SALES_HUB_GREEN} size={26} />
                            </div>
                          </Col>
                          <Col xs="8" className="d-flex align-items-center justify-content-end p-0 pr-3">
                            <div className="numbers text-right d-flex flex-column align-items-end" style={{ width: "100%" }}>
                              <div
                                className="btn-see-detail"
                                style={{ color: DASHBOARD_SALES_HUB_GREEN }}
                                onClick={() => navigate('?type=sales')}
                              >
                                {t("dashboard.salesDashboardLink")}
                              </div>
                              <p className="card-modern-title mb-1 mt-1">{t('dashboard.salesToday')}</p>
                              <h3
                                className="card-modern-value mb-0"
                                style={{ fontWeight: 700, fontSize: "20px", letterSpacing: "-0.02em", lineHeight: 1.15 }}
                              >
                                {salesHubSummary.totalFormatted}
                              </h3>
                              <DashboardHubTrendRow
                                summary={salesHubSummary}
                                vsLabelKey="dashboard.salesTrendVsPriorDay"
                                isLightTheme={isLightTheme}
                              />
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col lg="3" md="6" className="d-flex card-spacing-fix card-third" style={{ paddingLeft: '0px', paddingRight: '15px' }}>
                    <Card className="card-stats card-modern flex-fill dashboard-salesperson-stat-card" onClick={() => navigate('?type=salesperson')} style={{ cursor: 'pointer' }}>
                      <CardBody>
                        <Row style={{ height: "100%" }}>
                          <Col xs="4" className="d-flex align-items-center justify-content-start">
                            <div
                              className="d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: 50,
                                height: 50,
                                border: "none",
                                backgroundColor: `color-mix(in srgb, ${DASHBOARD_SALESPERSON_HUB_VIOLET} 14%, transparent)`,
                              }}
                              aria-hidden
                            >
                              <RiCalculatorFill color={DASHBOARD_SALESPERSON_HUB_VIOLET} size={26} />
                            </div>
                          </Col>
                          <Col xs="8" className="d-flex align-items-center justify-content-end p-0 pr-3">
                            <div className="numbers text-right d-flex flex-column align-items-end" style={{ width: "100%" }}>
                              <div
                                className="btn-see-detail"
                                style={{ color: DASHBOARD_SALESPERSON_HUB_VIOLET }}
                                onClick={() => navigate('?type=salesperson')}
                              >
                                {t("dashboard.salesPersonDashboardLink")}
                              </div>
                              <p className="card-modern-title mb-1 mt-1">{t("dashboard.salesPersonToday")}</p>
                              <h3
                                className="card-modern-value mb-0"
                                style={{ fontWeight: 700, fontSize: "20px", letterSpacing: "-0.02em", lineHeight: 1.15 }}
                              >
                                {salesPersonHubSummary.summaryText}
                              </h3>
                              <DashboardHubTrendRow
                                summary={salesPersonHubSummary}
                                vsLabelKey="dashboard.salesTrendVsPriorDay"
                                isLightTheme={isLightTheme}
                              />
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row className="align-items-stretch dashboard-analytics-chart-block">
                  {account?.permissions?.read_payments && (
                    <Col xs="12" className="card-spacing-fix">
                      <Card className="card-chart card-modern">
                        <CardHeader className="pb-0">
                          <Row className="mx-1 align-items-start">
                            <Col xs="12" md="6" lg="7" className="text-left p-0 pr-md-3 mb-3 mb-md-0">
                              <div className="d-flex align-items-center mb-2">
                                <i className="tim-icons icon-chart-bar-32 text-info mr-2" />
                                <h5 className="card-category mb-0">Total Transactions Statistics</h5>
                              </div>
                              <div className="dashboard-payments-total-amount mb-1">
                                ${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <CardTitle tag="h3" className="mb-1 card-modern-title" style={{ fontSize: "1rem" }}>
                                {bigChartData === "data1" ? "Weekly Activity" : bigChartData === "data2" ? "Monthly Activity" : "Yearly Activity"}: <span>{totalCount}</span>
                              </CardTitle>
                            
                            </Col>
                            <Col xs="12" md="6" lg="5" className="p-0 d-flex flex-column align-items-md-end align-items-stretch">
                              <div
                                className="btn-see-detail text-md-right mb-2"
                                onClick={() => navigate("/dashboard?type=payments")}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") navigate("/dashboard?type=payments");
                                }}
                              >
                                Transactions Dashboard <i className="tim-icons icon-double-right ml-1" style={{ fontSize: "0.6rem" }} />
                              </div>
                              <div className="d-flex flex-column align-items-md-end w-100" style={{ maxWidth: 380, marginLeft: "auto" }}>
                                <div className="d-flex flex-wrap align-items-center justify-content-md-end mb-2" style={{ gap: "10px" }}>
                                  <style>{`
                                    .dashboard-payments-chart-toggle-inner .dashboard-chart-type-btn {
                                      width: 32px;
                                      height: 32px;
                                      padding: 0;
                                      margin: 0;
                                      display: flex;
                                      align-items: center;
                                      justify-content: center;
                                      background: transparent !important;
                                      background-image: none !important;
                                      box-shadow: none !important;
                                      cursor: pointer;
                                      border: 1px solid ${isLightTheme ? "rgba(29, 37, 59, 0.22)" : "rgba(255, 255, 255, 0.22)"};
                                      color: ${isLightTheme ? "rgba(29, 37, 59, 0.42)" : "rgba(255, 255, 255, 0.42)"};
                                      transition: border-color 0.15s ease, color 0.15s ease;
                                    }
                                    .dashboard-payments-chart-toggle-inner .dashboard-chart-type-btn:hover:not(.active) {
                                      border-color: ${isLightTheme ? "rgba(29, 37, 59, 0.35)" : "rgba(255, 255, 255, 0.35)"};
                                      color: ${isLightTheme ? "rgba(29, 37, 59, 0.55)" : "rgba(255, 255, 255, 0.55)"};
                                    }
                                    .dashboard-payments-chart-toggle-inner .dashboard-chart-type-btn.active {
                                      border-color: ${activeColorStyle.borderColor};
                                      color: ${activeColorStyle.borderColor};
                                      position: relative;
                                      z-index: 1;
                                    }
                                    .dashboard-payments-chart-toggle-inner .dashboard-chart-type-btn + .dashboard-chart-type-btn {
                                      margin-left: -1px;
                                    }
                                    .dashboard-payments-chart-toggle-inner .dashboard-chart-type-btn:focus-visible {
                                      outline: 2px solid ${activeColorStyle.borderColor};
                                      outline-offset: 1px;
                                      z-index: 2;
                                    }
                                  `}</style>
                                  <div style={{ width: 68, flexShrink: 0 }} className="dashboard-payments-period-select">
                                    <Select
                                      activeColor={props.activeColor || "blue"}
                                      className="react-select"
                                      styles={paymentsPeriodSelectStyles}
                                      placeholder="7d"
                                      isSearchable={false}
                                      closeMenuOnSelect
                                      value={paymentsPeriodSelectValue}
                                      onChange={(opt) => opt?.value && setBgChartData(opt.value)}
                                      options={PAYMENTS_PERIOD_OPTIONS}
                                    />
                                  </div>
                                  <div
                                    className="dashboard-payments-chart-toggle-inner d-inline-flex flex-shrink-0"
                                    role="group"
                                    aria-label={t("dashboard.chartTypeSwitcher")}
                                  >
                                    <button
                                      type="button"
                                      className={classNames("dashboard-chart-type-btn", { active: paymentsChartKind === "line" })}
                                      onClick={() => setPaymentsChartKind("line")}
                                      aria-pressed={paymentsChartKind === "line"}
                                      title={t("dashboard.chartLine")}
                                    >
                                      <MdShowChart size={16} />
                                    </button>
                                    <button
                                      type="button"
                                      className={classNames("dashboard-chart-type-btn", { active: paymentsChartKind === "bar" })}
                                      onClick={() => setPaymentsChartKind("bar")}
                                      aria-pressed={paymentsChartKind === "bar"}
                                      title={t("dashboard.chartBar")}
                                    >
                                      <MdBarChart size={16} />
                                    </button>
                                  </div>
                                </div>
                                <div className="d-flex flex-wrap justify-content-md-end align-items-center" style={{ gap: "12px 16px" }}>
                                  {paymentsLegendItems.map((item) => (
                                    <div key={item.key} className="d-flex align-items-center">
                                      <span
                                        style={{
                                          width: 10,
                                          height: 10,
                                          backgroundColor: item.color,
                                          marginRight: 8,
                                          flexShrink: 0,
                                          display: "inline-block",
                                        }}
                                      />
                                      <span className="mb-0" style={{ fontSize: 12, fontWeight: 500, opacity: 0.92 }}>
                                        {item.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </CardHeader>
                        <CardBody>
                          <div className="chart-area" style={{ height: "300px", position: "relative" }}>
                            {isStatsLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
                            {paymentsChartKind === "line" ? (
                              <Line
                                key={`${isStatsLoading}-line-${bigChartData}`}
                                data={
                                  isStatsLoading
                                    ? {
                                        labels: ["-", "-", "-", "-", "-"],
                                        datasets: [
                                          {
                                            data: [0, 0, 0, 0, 0],
                                            borderColor: "#3b82f6",
                                            borderWidth: 2.5,
                                            fill: true,
                                            backgroundColor: "rgba(59, 130, 246, 0.15)",
                                            tension: 0.42,
                                            pointRadius: 0,
                                          },
                                        ],
                                      }
                                    : dynamicChartData[bigChartData]
                                }
                                options={getPaymentsLineOrBarOptions("line")}
                              />
                            ) : (
                              <Bar
                                key={`${isStatsLoading}-bar-${bigChartData}`}
                                data={
                                  isStatsLoading
                                    ? {
                                        labels: ["-", "-", "-", "-", "-"],
                                        datasets: [
                                          {
                                            label: "",
                                            data: [0, 0, 0, 0, 0],
                                            backgroundColor: "rgba(59, 130, 246, 0.3)",
                                            borderColor: "#3b82f6",
                                            borderWidth: 2,
                                          },
                                        ],
                                      }
                                    : paymentsBarChartData
                                }
                                options={getPaymentsLineOrBarOptions("bar")}
                              />
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  )}
                  <Col lg="4" className="card-spacing-fix d-flex" style={{ paddingLeft: '15px', paddingRight: '10px' }}>
                    <Card className="card-chart card-modern w-100 d-flex flex-column">
                      <CardHeader>
                        <Row className="justify-content-between align-items-center mx-0 mb-3">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-delivery-fast text-primary mr-2" />
                            <h5 className="card-category mb-0">Inventory By Location</h5>
                          </div>
                          <div className="btn-see-detail" onClick={() => navigate('/dashboard?type=inventory')}>
                            Details <i className="tim-icons icon-double-right ml-1" style={{ fontSize: '14px' }} />
                          </div>
                        </Row>
                        <div className="d-flex justify-content-between px-0">
                          <span className="card-modern-title"># Vehicles In Stock (Active)</span>
                          <span className="card-modern-value">{statistics?.inventory_by_location_monthly?.total_available_vehicles}</span>
                        </div>
                      </CardHeader>
                      <CardBody className="d-flex flex-column flex-grow-1">
                        <div
                          className="chart-area flex-grow-1 d-flex"
                          style={{ position: 'relative', minHeight: 200, minWidth: 0 }}
                        >
                          {isStatsLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
                          <Bar
                            key={isStatsLoading ? 'loading' : 'ready'}
                            data={isStatsLoading ? { labels: ["-", "-", "-"], datasets: [{ data: [0, 0, 0], backgroundColor: '#1d8cf8' }] } : vehicleChartData}
                            options={{
                              ...vehicleChartOptions,
                              animation: {
                                duration: 1000,
                                easing: 'easeOutQuart',
                                delay: (c) => c.dataIndex * 100
                              }
                            }}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col lg="4" className="card-spacing-fix d-flex" style={{ paddingLeft: '0px', paddingRight: '10px' }}>
                    <Card className="card-chart card-modern w-100 d-flex flex-column">
                      <CardHeader>
                        <Row className="justify-content-between align-items-center mx-0 mb-3">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-time-alarm text-danger mr-2" />
                            <h5 className="card-category mb-0">Past Due Accounts</h5>
                          </div>
                          <div className="btn-see-detail" onClick={() => navigate('/dashboard?type=iq')}>
                            Details <i className="tim-icons icon-double-right ml-1" style={{ fontSize: '0.6rem' }} />
                          </div>
                        </Row>
                        <div className="d-flex justify-content-between px-0">
                          <span className="card-modern-title">Total Payments Past Due</span>
                          <span className="card-modern-value">{totalPastDue}</span>
                        </div>
                      </CardHeader>
                      <CardBody className="d-flex flex-column flex-grow-1">
                        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                          <div
                            className="chart-area d-flex justify-content-center align-items-center"
                            style={{ position: 'relative', minHeight: 210, minWidth: 0 }}
                          >
                            {isStatsLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
                            <div style={{ width: '100%', maxWidth: 380, height: 210 }}>
                              <Doughnut
                                ref={pastDueChartRef}
                                key={`${isStatsLoading ? 'loading' : 'ready'}-${isLightTheme ? 'light' : 'dark'}`}
                                data={isStatsLoading ? { labels: ["-", "-", "-"], datasets: [{ data: [1, 1, 1], backgroundColor: ['#2b354e', '#2b354e', '#2b354e'] }] } : pastDueChartData}
                                options={{
                                  ...pastDueChartOptions,
                                  animation: {
                                    duration: 1000,
                                    easing: 'easeOutQuart',
                                    delay: (c) => c.dataIndex * 120
                                  },
                                  plugins: { ...pastDueChartOptions.plugins, tooltip: { enabled: !isStatsLoading } }
                                }}
                                plugins={[doughnutLabelsLine]}
                              />
                            </div>
                          </div>
                          <div
                            className="past-due-legend"
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '0 28px',
                              padding: '6px 6px 0',
                            }}
                          >
                            {pastDueLegendColumns.map((column, columnIndex) => (
                              <div
                                key={`column-${columnIndex}`}
                                style={{
                                  width: '220px',
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '10px',
                                }}
                              >
                                {column.map((item) => (
                                  <div
                                    key={item.label}
                                    className="d-flex align-items-start"
                                    style={{ minWidth: 0, cursor: 'pointer' }}
                                    onMouseEnter={() => setPastDueLegendHover(item.index)}
                                    onMouseLeave={() => setPastDueLegendHover(null)}
                                  >
                                    <div
                                      style={{
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: item.color,
                                        marginTop: '4px',
                                        marginRight: '10px',
                                        flexShrink: 0,
                                      }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: '9px',
                                          fontWeight: 500,
                                          color: isLightTheme ? '#64748b' : 'rgba(255,255,255,0.72)',
                                          lineHeight: 1.25,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {item.label}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '13px',
                                          fontWeight: 700,
                                          color: isLightTheme ? '#0f172a' : '#ffffff',
                                          lineHeight: 1.1,
                                          marginTop: '2px',
                                        }}
                                      >
                                        {item.value}
                                        <span
                                          style={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            marginLeft: '6px',
                                            color: isLightTheme ? '#475569' : 'rgba(255,255,255,0.8)',
                                          }}
                                        >
                                          ({item.percentage}%)
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col lg="4" className="card-spacing-fix d-flex" style={{ paddingLeft: '0px', }}>
                    <Card className="card-chart card-modern w-100 d-flex flex-column">
                      <CardHeader>
                        <Row className="justify-content-between align-items-center mx-0 mb-3">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-money-coins text-success mr-2" />
                            <h5 className="card-category mb-0">{t('dashboard.salesToday')}</h5>
                          </div>
                          <div className="btn-see-detail" onClick={() => navigate('/dashboard?type=sales')}>
                            {t('dashboard.details')} <i className="tim-icons icon-double-right ml-1" style={{ fontSize: '0.6rem' }} />
                          </div>
                        </Row>
                        <div className="d-flex justify-content-between px-0">
                          <span className="card-modern-title">{t('dashboard.salesTodayRevenue')}</span>
                          <span className="card-modern-value">${(salesTodayObj.total_data || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </CardHeader>
                      <CardBody className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                        <RevenueByLocationMap
                          title={null}
                          isLightTheme={isLightTheme}
                          baseColor={getButtonColor(activeColor)}
                          data={statistics?.locations || statistics?.inventory?.locations || statistics?.inventory?.by_state || []}
                          valueLabel={t('dashboard.vehicles', { defaultValue: 'vehicles' })}
                          fillContainer
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                {(canReadAnalytics || hasLogsRead) && (
                <Row className="dashboard-logs-section">
                {hasLogsRead && (
                    <Col lg={canReadAnalytics ? "6" : "12"} className="card-spacing-fix dashboard-logs-col-left">
                      <Card className="card-chart card-modern dashboard-split-panel-card">
                        <CardHeader>
                          <Row className="justify-content-between align-items-center mx-0 mb-0">
                            <div className="d-flex align-items-center">
                              <i className="tim-icons icon-bullet-list-67 text-info mr-2" />
                              <h5 className="card-category mb-0">{t('dashboard.logs')}</h5>
                            </div>
                            <div className="btn-see-detail flex-shrink-0" onClick={() => navigate('/logs')}>
                              {t('dashboard.viewAllLogs')} <i className="tim-icons icon-double-right ml-1" style={{ fontSize: '0.6rem' }} />
                            </div>
                          </Row>
                        </CardHeader>
                        <CardBody className="dashboard-split-panel-body">
                          <div className="dashboard-logs-scroll">
                          <Table responsive className="table-modern -striped mb-0">
                            <thead>
                              <tr>
                                <th>{t('dashboard.user')}</th>
                                <th>{t('dashboard.action')}</th>
                                <th>{t('dashboard.date')}</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {logs?.results?.map((log, index) => {
                                const logId = log.id || `log-${index}`;
                                const isExpanded = !!expandedRows[logId];
                                const hasChangedData = log.changed_data && (
                                  (typeof log.changed_data === 'object' && Object.keys(log.changed_data).length > 0) ||
                                  (typeof log.changed_data === 'string' && log.changed_data.length > 0)
                                );

                                return (
                                  <React.Fragment key={logId}>
                                    <tr>
                                      <td>
                                        <span className="log-user-tag" style={{ borderRadius: '0px' }} onClick={() => navigate(`/users/${log?.user_id}`)}>
                                          {log?.user_name}
                                        </span>
                                      </td>
                                      <td>
                                        {log?.link ? (
                                          <span
                                            className="log-action-link"
                                            onClick={() => navigate(log.link)}
                                          >
                                            {log?.action}
                                          </span>
                                        ) : (
                                          <span className="font-weight-600">{log?.action}</span>
                                        )}
                                      </td>
                                      <td className="text-muted">
                                        {formatDateTimeForDisplay(log?.created_at || log?.createdAt)}
                                      </td>
                                      <td className="text-center">
                                        {hasChangedData ? (
                                          <span
                                            className="btn-see-detail"
                                            style={{ whiteSpace: 'nowrap' }}
                                            onClick={() => toggleExpand(logId)}
                                          >
                                            {isExpanded ? t('dashboard.hide') : t('dashboard.details')}
                                            <i className={`tim-icons ${isExpanded ? 'icon-minimal-up' : 'icon-minimal-down'} ml-1`} style={{ fontSize: '0.6rem' }} />
                                          </span>
                                        ) : (
                                          <span className="text-muted small">-</span>
                                        )}
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr className="p-0">
                                        <td colSpan="4" className="expanded-row-data" >
                                            {renderChangedData(log.changed_data)}
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                )}
                    {canReadAnalytics && (
                    <Col lg={hasLogsRead ? "6" : "12"} className="card-spacing-fix dashboard-logs-col-right">
                      <Card className="card-chart card-modern dashboard-split-panel-card">
                        <CardBody className="dashboard-split-panel-body dashboard-split-panel-body--flush">
                          <DashboardScheduleExportsCalendar
                            fillHeight
                            activeColorStyle={activeColorStyle}
                            isLightTheme={isLightTheme}
                            enabled={canReadAnalytics}
                            onReportClick={(reportId) => navigate(`/reports/${reportId}`)}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                    )}
                </Row>
                )}
                {statistics?.web_traffic?.web_enabled ? (
                  <DashboardDailyWebTraffic
                    data={statistics.web_traffic}
                    isLoading={isStatsLoading}
                    isLightTheme={isLightTheme}
                    onDetails={() => navigate("/website-performance")}
                  />
                ) : null}
              </>
            )}
          </>
        ) : (
          <div className="quick-menu-grid">
            <style>{`
          .quick-menu-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 15px;
                width: 100%;
                max-width: 1400px;
                margin: 0 auto;
              }
              .quick-menu-item {
                background: linear-gradient(145deg, #0f1118, #050505);
                border-radius: 0px;
                flex: 0 0 calc(14.28% - 18px);
                min-width: 210px;
                aspect-ratio: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                border: 1px solid #1a1b23;
                text-decoration: none !important;
                padding: 20px;
                position: relative;
                overflow: hidden;
              }
              .quick-menu-item::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: radial-gradient(circle at center, rgba(31, 142, 241, 0.1) 0%, transparent 70%);
                opacity: 0;
                transition: opacity 0.3s ease;
              }
              .quick-menu-item:hover {
                transform: scale(1.03);
                border-color: #1f8ef1;
                background: linear-gradient(145deg, #161a25, #0a0a0d);
                box-shadow: 0 0 30px rgba(31, 142, 241, 0.2);
                z-index: 2;
                outline: 1px solid #1f8ef1;
              }
              .quick-menu-item:hover::before {
                opacity: 1;
              }
              .quick-menu-icon {
                color: #1f8ef1;
                margin-bottom: 15px;
                font-size: 55px;
                display: flex;
                align-items: center;
                justify-content: center;
                filter: drop-shadow(0 0 8px rgba(31, 142, 241, 0.3));
                transition: transform 0.3s ease;
              }
              .quick-menu-item:hover .quick-menu-icon {
                transform: translateY(-5px);
                filter: drop-shadow(0 0 12px rgba(31, 142, 241, 0.6));
              }
              .quick-menu-text {
                color: #fff;
                font-size: 13px;
                font-weight: 600;
                text-align: center;
                line-height: 1.2;
                text-transform: uppercase;
                letter-spacing: 1px;
                opacity: 0.8;
                transition: opacity 0.3s ease;
              }
              .quick-menu-item:hover .quick-menu-text {
                opacity: 1;
                color: #1f8ef1;
              }
              @media (max-width: 1600px) {
                .quick-menu-item { flex: 0 0 calc(20% - 15px); }
              }
              @media (max-width: 1200px) {
                .quick-menu-item { flex: 0 0 calc(25% - 15px); }
              }
              @media (max-width: 992px) {
                .quick-menu-item { flex: 0 0 calc(33.33% - 15px); }
              }
              @media (max-width: 768px) {
                .quick-menu-item { flex: 0 0 calc(50% - 15px); }
              }
              @media (max-width: 480px) {
                .quick-menu-item { flex: 0 0 100%; }
              }
            `}</style>
            {tempMenuItems
              .filter(item => !item.permission || (account?.permissions && account.permissions[item.permission]))
              .map((item, index) => (
                <div
                  key={item.id}
                  className="quick-menu-item"
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={(e) => handleDragOver(e, item.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => {
                    if (isEditMode) return;
                    if (item.onClick) item.onClick();
                    else navigate(item.path);
                  }}
                  style={{
                    position: 'relative',
                    cursor: isEditMode ? 'move' : 'pointer',
                    opacity: draggedId === item.id ? 0 : 1,
                    transition: 'transform 0.2s ease, opacity 0.2s ease'
                  }}
                >
                  {isEditMode && (
                    <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: '8px', zIndex: 3 }}>
                      <div
                        style={{ color: '#1f8ef1', cursor: 'pointer', padding: '5px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '4px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingItemId(item.id);
                          setEditingValue(item.name);
                        }}
                      >
                        <MdEdit size={18} />
                      </div>
                      <div
                        style={{ color: '#f5365c', cursor: 'pointer', padding: '5px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '4px' }}
                        onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                      >
                        <MdClose size={18} />
                      </div>
                    </div>
                  )}
                  <div className="quick-menu-icon" style={{ opacity: isEditMode ? 0.6 : 1, pointerEvents: 'none' }}>{item.icon}</div>

                  {editingItemId === item.id ? (
                    <div className="w-100 px-3" onClick={e => e.stopPropagation()}>
                      <Input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => renameItem(item.id, editingValue)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renameItem(item.id, editingValue);
                          if (e.key === 'Escape') setEditingItemId(null);
                        }}
                        autoFocus
                        style={{
                          fontSize: '11px', textAlign: 'center', height: '28px',
                          background: '#1a1b23', border: '1px solid #1f8ef1', color: '#fff'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="quick-menu-text" style={{ opacity: isEditMode ? 0.6 : 1, pointerEvents: 'none' }}>{item.name}</div>
                  )}
                </div>
              ))}

            {isEditMode && (
              <div
                className="quick-menu-item add-favorite"
                onClick={() => setShowAddModal(true)}
                style={{
                  background: 'linear-gradient(145deg, #1e222d, #14161c)',
                  border: '2px dashed #1f8ef1',
                  cursor: 'pointer',
                  opacity: 0.8,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <div className="quick-menu-icon" style={{ color: '#1f8ef1', margin: 0 }}><MdAdd size={40} /></div>
                <div className="quick-menu-text" style={{ color: '#1f8ef1', opacity: 1, fontSize: '11px' }}>{t('dashboard.addFavorite')}</div>
              </div>
            )}

            {showAddModal && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px', backdropFilter: 'blur(5px)'
              }}>
                <Card style={{
                  maxWidth: '800px', width: '100%', maxHeight: '85vh',
                  backgroundColor: '#11131a', border: '1px solid #1f8ef1',
                  boxShadow: '0 0 40px rgba(31, 142, 241, 0.2)',
                  borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column'
                }}>
                  <div style={{
                    padding: '25px', borderBottom: '1px solid rgba(31, 142, 241, 0.1)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'linear-gradient(to right, #11131a, #1a1e2b)'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{t('dashboard.addItemsToQuickMenu')}</h3>
                    <div
                      onClick={() => setShowAddModal(false)}
                      style={{ cursor: 'pointer', color: '#fff', opacity: 0.6, transition: 'opacity 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                    >
                      <MdClose size={28} />
                    </div>
                  </div>
                  <CardBody style={{
                    overflowY: 'auto', padding: '30px',
                    background: '#0c0d12'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: '20px'
                    }}>
                      {defaultItems
                        .filter(item => !tempMenuItems.find(t => t.id === item.id))
                        .filter(item => !item.permission || (account?.permissions && account.permissions[item.permission]))
                        .map(item => (
                          <div
                            key={item.id}
                            className="quick-menu-item-selection"
                            onClick={() => addItem(item)}
                            style={{
                              background: 'linear-gradient(145deg, #11131a, #090a0d)',
                              borderRadius: '8px', cursor: 'pointer', padding: '25px',
                              border: '1px solid #1a1b23', transition: 'all 0.3s ease',
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              justifyContent: 'center', gap: '15px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#1f8ef1';
                              e.currentTarget.style.transform = 'translateY(-5px)';
                              e.currentTarget.style.boxShadow = '0 10px 20px rgba(31, 142, 241, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#1a1b23';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div className="quick-menu-icon" style={{ fontSize: '40px', color: '#1f8ef1', margin: 0, filter: 'drop-shadow(0 0 8px rgba(31, 142, 241, 0.3))' }}>{item.icon}</div>
                            <div className="quick-menu-text" style={{ fontSize: '11px', color: '#fff', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>{item.name}</div>
                          </div>
                        ))
                      }
                      {defaultItems.filter(item => !tempMenuItems.find(t => t.id === item.id)).length === 0 && (
                        <div className="text-center w-100 p-5 mt-4" style={{ color: 'rgba(255,255,255,0.4)', gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: '40px', marginBottom: '15px' }}><MdDashboard size={50} /></div>
                          <div style={{ fontSize: '15px', letterSpacing: '0.5px' }}>{t('dashboard.allModulesActive')}</div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}
          </div>
        )}
        <DashboardRightDrawer ref={drawerRef} isLightTheme={isLightTheme} activeColor={activeColor} />
      </div >
    </>
  );
};

const DashboardRightDrawer = React.forwardRef(({ isLightTheme, activeColor }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);
  const [newDashboardName, setNewDashboardName] = React.useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mutate: createDashboard, isLoading: isCreating } = useCreateDashboard();
  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard();
  const { mutate: deleteDashboard } = useDeleteDashboard();
  const [editingId, setEditingId] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 992);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [alert, setAlert] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('active');
  const { data: dynamicDashboards } = useGetDashboards({ status: statusFilter });

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setIsVisible(true);
      setTimeout(() => setIsOpen(true), 10);
    },
    close: () => {
      setIsOpen(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }));

  if (!isVisible) return null;

  const dashboardItems = [
    { name: 'INVENTORY DASHBOARD', type: 'inventory' },
    { name: 'SALES DASHBOARD', type: 'sales' },
    { name: 'SALES PERSON DASHBOARD', type: 'salesperson' },
    { name: 'IQ DASHBOARD', type: 'iq' },
  ];

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: isOpen ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
        zIndex: 9999, transition: 'background-color 300ms ease',
        display: 'flex', justifyContent: 'flex-end'
      }}
      onClick={() => { setIsOpen(false); setTimeout(() => setIsVisible(false), 300); }}
    >
      <div
        style={{
          width: isMobile ? '100%' : '40vw', height: '100%',
          backgroundColor: isLightTheme ? '#fff' : '#1e1e2f',
          color: isLightTheme ? '#111' : '#fff',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.4)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex', flexDirection: 'column',
          padding: '0px'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid rgba(136, 136, 136, 0.1)',
          background: isLightTheme ? '#f8f9fa' : 'rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {(isAdding || editingId) && (
              <i
                className="tim-icons icon-minimal-left"
                style={{ cursor: 'pointer', fontSize: '14px', color: '#1d8cf8' }}
                onClick={() => { setIsAdding(false); setEditingId(null); setNewDashboardName(''); }}
              />
            )}
            <h4 className="mb-0" style={{ fontWeight: '800', fontSize: '15px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1d8cf8' }}>
              {isAdding ? 'New Dashboard' : editingId ? 'Edit Dashboard' : 'Select Dashboard'}
            </h4>
          </div>
          <MdClose
            size={20}
            style={{ cursor: 'pointer', opacity: 0.6 }}
            onClick={() => { setIsOpen(false); setIsAdding(false); setEditingId(null); setTimeout(() => setIsVisible(false), 300); }}
          />
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {(!isAdding && !editingId) ? (
            <>
              <div style={{ marginBottom: '10px' }}>
                <div
                  className="add-dashboard-btn"
                  onClick={() => setIsAdding(true)}
                  style={{
                    height: '48px', fontWeight: 'bold', fontSize: '12px',
                    border: '1px dashed rgba(136, 136, 136, 0.3)', color: '#888',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    cursor: 'pointer', transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#1d8cf8';
                    e.currentTarget.style.color = '#1d8cf8';
                    e.currentTarget.style.backgroundColor = 'rgba(29, 140, 248, 0.03)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(136, 136, 136, 0.3)';
                    e.currentTarget.style.color = '#888';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <MdAdd size={18} />
                  ADD NEW DASHBOARD
                </div>
              </div>
              <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid rgba(136, 136, 136, 0.5)' }}>
                <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                  Filter Status
                </label>
                <Select
                  activeColor={activeColor || "blue"}
                  value={{ value: statusFilter, label: statusFilter.toUpperCase() }}
                  options={[
                    { value: 'all', label: t('dashboard.all').toUpperCase() },
                    { value: 'active', label: t('dashboard.active').toUpperCase() },
                    { value: 'deleted', label: t('dashboard.deleted').toUpperCase() },
                  ]}
                  onChange={(opt) => setStatusFilter(opt.value)}
                  isSearchable={false}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Static Dashboards */}
                {dashboardItems.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      navigate(`?type=${item.type}`);
                      setIsOpen(false);
                      setTimeout(() => setIsVisible(false), 300);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '10px 20px', borderRadius: '4px',
                      backgroundColor: isLightTheme ? '#fff' : 'rgba(255,255,255,0.03)',
                      border: isLightTheme ? '1px solid #e9ecef' : '1px solid rgba(255,255,255,0.08)',
                      boxShadow: isLightTheme ? '0 1px 3px rgba(0,0,0,0.03)' : 'none',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      marginBottom: '4px'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = isLightTheme ? '#fff' : 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.borderColor = '#1d8cf8';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isLightTheme ? '0 6px 12px rgba(0,0,0,0.08)' : '0 6px 12px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = isLightTheme ? '#fff' : 'rgba(255,255,255,0.03)';
                      e.currentTarget.style.borderColor = isLightTheme ? '#e9ecef' : 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isLightTheme ? '0 1px 3px rgba(0,0,0,0.03)' : 'none';
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '13px', letterSpacing: '0.8px', color: isLightTheme ? '#444' : 'rgba(255,255,255,0.9)' }}>{item.name}</div>
                    <div style={{ marginLeft: 'auto', opacity: 0.4 }}>
                      <i className="tim-icons icon-minimal-right" style={{ fontSize: '12px' }} />
                    </div>
                  </div>
                ))}

                {/* Dynamic Dashboards */}
                {dynamicDashboards?.results?.map((item, idx) => (
                  <div
                    key={`dynamic-${idx}`}
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '10px 20px', borderRadius: '4px',
                      backgroundColor: isLightTheme ? '#fff' : 'rgba(255,255,255,0.03)',
                      border: isLightTheme ? '1px solid #e9ecef' : '1px solid rgba(255,255,255,0.08)',
                      boxShadow: isLightTheme ? '0 1px 3px rgba(0,0,0,0.03)' : 'none',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      marginBottom: '4px',
                      position: 'relative',
                      group: 'item'
                    }}
                    className="dashboard-list-item"
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = isLightTheme ? '#fff' : 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.borderColor = '#1d8cf8';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isLightTheme ? '0 6px 12px rgba(0,0,0,0.08)' : '0 6px 12px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = isLightTheme ? '#fff' : 'rgba(255,255,255,0.03)';
                      e.currentTarget.style.borderColor = isLightTheme ? '#e9ecef' : 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isLightTheme ? '0 1px 3px rgba(0,0,0,0.03)' : 'none';
                    }}
                    onClick={() => {
                      navigate(`?type=${item?.dashboard_id}`);
                      setIsOpen(false);
                      setTimeout(() => setIsVisible(false), 300);
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '13px', letterSpacing: '0.8px', color: isLightTheme ? '#444' : 'rgba(255,255,255,0.9)' }}>{item.name?.toUpperCase()}</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(item.dashboard_id);
                          setNewDashboardName(item.name);
                        }}
                        style={{ padding: '6px', cursor: 'pointer', color: '#1d8cf8', opacity: 0.6 }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                      >
                        <MdEdit size={16} />
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setAlert(
                            <SweetAlert
                              warning
                              style={{ display: "block", marginTop: "-100px", color: "#333" }}
                              title="Are you sure?"
                              onConfirm={() => {
                                deleteDashboard(item.dashboard_id);
                                setAlert(null);
                              }}
                              onCancel={() => setAlert(null)}
                              confirmBtnBsStyle="danger"
                              confirmBtnStyle={{ backgroundColor: "#ec250d !important", backgroundImage: "none !important" }}
                              cancelBtnBsStyle="secondary"
                              confirmBtnText="Yes, delete it!"
                              cancelBtnText="Cancel"
                              showCancel
                            >
                              You are about to delete this dashboard.
                            </SweetAlert>
                          );
                        }}
                        style={{ padding: '6px', cursor: 'pointer', color: '#fd5d93', opacity: 0.6 }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                      >
                        <MdClose size={16} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <style>{`
                @keyframes fadeIn {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>
              <div className="form-group mb-4">
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#1d8cf8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                  {editingId ? 'Update Dashboard Name' : 'Dashboard Name'}
                </label>
                <Input
                  type="text"
                  placeholder="Enter dashboard name..."
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  style={{
                    borderRadius: '4px',
                    height: '50px',
                    background: isLightTheme ? '#fff' : 'rgba(0,0,0,0.2)',
                    border: '1px solid rgba(136, 136, 136, 0.2)',
                    fontSize: '14px',
                    color: isLightTheme ? '#111' : '#fff'
                  }}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  style={{ borderRadius: '4px', height: '36px', fontWeight: 'bold', fontSize: '11px', padding: '0 20px', margin: 0 }}
                  onClick={() => {
                    setIsAdding(false);
                    setEditingId(null);
                    setNewDashboardName('');
                  }}
                >
                  CANCEL
                </Button>
                <Button
                  color="info"
                  size="sm"
                  disabled={isCreating || isUpdating || !newDashboardName.trim()}
                  style={{ borderRadius: '4px', height: '36px', fontWeight: 'bold', fontSize: '11px', padding: '0 25px', margin: 0 }}
                  onClick={() => {
                    if (editingId) {
                      updateDashboard({ id: editingId, data: { name: newDashboardName } }, {
                        onSuccess: () => {
                          setEditingId(null);
                          setNewDashboardName('');
                        }
                      });
                    } else {
                      createDashboard({ name: newDashboardName }, {
                        onSuccess: () => {
                          setIsAdding(false);
                          setNewDashboardName('');
                        }
                      });
                    }
                  }}
                >
                  {isCreating || isUpdating ? 'SAVING...' : editingId ? 'UPDATE' : 'SAVE'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {alert}
    </div >
  );
});

export default Dashboard;
